import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Button, Form, Modal, Table } from 'react-bootstrap';
import CustomPagination from 'components/CustomPagination';
import { CustomLoading } from 'components/CustomLoading';
import { AddAdvisorModal, AdvisorDetailsModal } from './AdvisorModals';
import toastr from 'toastr';
import { Mail, Search, ChevronUp, ChevronDown, UserPlus, Eye } from 'lucide-react';
import AdvisorViewLink from './AdvisorViewLink';

const filterAdvisors = (list, query) => {
    const q = (query ?? '').toLowerCase();
    if (!q) return list;
    return list.filter((item) =>
        item.healthClaimAccountId?.toString().includes(q) ||
        item.healthClaimAdvisorId?.toString().includes(q) ||
        item.healthClaimRelationship?.toLowerCase().includes(q) ||
        item.healthClaimDescription?.toLowerCase().includes(q) ||
        item.lastName?.toLowerCase().includes(q) ||
        item.firstName?.toLowerCase().includes(q) ||
        item.email?.toLowerCase().includes(q) ||
        item.user_Id?.toString().includes(q)
    );
};

const AdvisorsReport = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage] = useState(12);
    const [showModal, setShowModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortColumn, setSortColumn] = useState('healthClaimAccountId');
    const [sortDirection, setSortDirection] = useState('asc');
    const [rmdList, setRmdList] = useState([]);
    const [bddList, setBddList] = useState([]);
    const [showAddAdvisorModal, setShowAddAdvisorModal] = useState(false);
    const [showToggleConfirmModal, setShowToggleConfirmModal] = useState(false);
    const [distroList, setDistroList] = useState([]);
    const [savingNdpId, setSavingNdpId] = useState(null);
    const [ndpConfirm, setNdpConfirm] = useState(null);
    const [provinceDirectors, setProvinceDirectors] = useState(null);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const response = await axios.get('/api/Advisors/advisors');
            const { advisors, rmdList, bddList } = response.data;
            setData(advisors);
            setFilteredData(filterAdvisors(advisors, searchQuery));
            setRmdList(rmdList);
            setBddList(bddList);

            const distroResponse = await axios.get('/api/Advisors/distroaccounts');
            setDistroList(distroResponse.data.distroAccounts)

            setLoading(false);
        } catch (error) {
            toastr.error("Failed to fetch advisor details", error);
        }

        try {
            const directorsResponse = await axios.get('/api/JgDirectors');
            const map = {};
            (directorsResponse.data.provinces ?? []).forEach((row) => {
                map[row.province] = { rmd: row.rmd, bdd1: row.bdd1, bdd2: row.bdd2 };
            });
            setProvinceDirectors(map);
        } catch (error) {
            setProvinceDirectors(null);
            toastr.warning("RMD/BDD province assignments unavailable — directors will not auto-fill");
        }
    };

    const handleSearch = (event) => {
        const query = event.target.value.toLowerCase();
        setSearchQuery(query);
        setFilteredData(filterAdvisors(data, query));
        setCurrentPage(1);
    };

    const handleSort = (column) => {
        const direction = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc';
        const sorted = [...filteredData].sort((a, b) => {
            if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
            if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
            return 0;
        });
        setSortColumn(column);
        setSortDirection(direction);
        setFilteredData(sorted);
    };

    const handleShowDetails = (item) => {
        setSelectedItem(item);
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedItem(null);
        fetchData();
    };

    const handleCloseAddAdvisorModal = () => {
        setShowAddAdvisorModal(false);
        fetchData();
    };

    const currentRecords = useMemo(() => {
        const indexOfLastRecord = currentPage * itemsPerPage;
        const indexOfFirstRecord = indexOfLastRecord - itemsPerPage;
        return filteredData.slice(indexOfFirstRecord, indexOfLastRecord);
    }, [currentPage, itemsPerPage, filteredData]);

    const nPages = useMemo(() => {
        return Math.ceil(filteredData.length / itemsPerPage);
    }, [filteredData.length, itemsPerPage]);

    const handleAdvisorUpdate = (updatedA1Number, updatedRmd1, updatedBdd1, updatedBdd2, updatedEmail) => {
        setSelectedItem((prev) => ({
            ...prev,
            a1number: updatedA1Number,
            rmd1DistroId: updatedRmd1,
            bdd1DistroId: updatedBdd1,
            bdd21DistroId: updatedBdd2,
            email: updatedEmail
        }));

        setData((prevData) =>
            prevData.map((item) =>
                item.id === selectedItem.id
                    ? { ...item, a1number: updatedA1Number, rmd1DistroId: updatedRmd1, email: updatedEmail }
                    : item
            )
        );

        setFilteredData((prevFilteredData) =>
            prevFilteredData.map((item) =>
                item.id === selectedItem.id
                    ? { ...item, a1number: updatedA1Number, rmd1DistroId: updatedRmd1, email: updatedEmail }
                    : item
            )
        );
    };

    const applyNdpToRow = (id, value) => {
        const patch = (rows) =>
            rows.map((row) =>
                row.id === id ? { ...row, isNationalDistributionPartner: value } : row
            );
        setData(patch);
        setFilteredData(patch);
    };

    const requestNdpChange = (item) => {
        setNdpConfirm({ item, nextValue: !item.isNationalDistributionPartner });
    };

    const confirmNdpChange = async () => {
        const { item, nextValue } = ndpConfirm;
        setNdpConfirm(null);
        setSavingNdpId(item.id);
        applyNdpToRow(item.id, nextValue);

        try {
            await axios.put('/api/Advisors/updatenationaldistributionpartner', {
                id: item.id,
                isNationalDistributionPartner: nextValue,
            });
            toastr.success(
                `${item.firstName} ${item.lastName} ${nextValue ? 'marked as' : 'removed as'} a National Distribution Partner`
            );
        } catch (e) {
            applyNdpToRow(item.id, item.isNationalDistributionPartner);
            toastr.error("Failed to update National Distribution Partner");
        } finally {
            setSavingNdpId(null);
        }
    };

    const handleConfirmation = (item) => {
        setShowToggleConfirmModal(true);
        setSelectedItem(item);
    }

    const sendEmail = async () => {
        try {
            await axios.post("/api/Advisors/sendemail", selectedItem.email, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            toastr.success(`Successfully sent welcome email to ${selectedItem.email}`);
            setShowToggleConfirmModal(false);
        }
        catch (e) {
            toastr.error("Failed to send welcome email.", e);
        }
    };

    const renderSortIcon = (column) => {
        if (sortColumn !== column) return null;
        return sortDirection === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />;
    };

    return (
        <>
            {loading ? (
                <CustomLoading />
            ) : (
                <>
                    <AdvisorDetailsModal
                        showModal={showModal}
                        onHide={handleCloseModal}
                        selectedItem={selectedItem}
                        rmdList={rmdList}
                        bddList={bddList}
                        distroList={distroList}
                        advisorList={data}
                        provinceDirectors={provinceDirectors}
                    />
                    <AddAdvisorModal
                        advisors={data}
                        distroList={distroList}
                        rmdList={rmdList}
                        bddList={bddList}
                        provinceDirectors={provinceDirectors}
                        showModal={showAddAdvisorModal}
                        onHide={handleCloseAddAdvisorModal}
                    />
                    <div className="advisor-search-wrapper">
                        <Search size={16} />
                        <Form.Control
                            type="text"
                            placeholder="Search by name, ID, email, or relationship..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="advisor-search-input"
                        />
                    </div>
                    <Button variant="primary" size="sm" className="search-action-btn mb-3" onClick={() => setShowAddAdvisorModal(true)}>
                        <UserPlus size={14} /> Add Advisor
                    </Button>
                    <div className="dashboard-table-container">
                        <Table hover className="table table-hover search-results-table">
                            <thead>
                                <tr>
                                    <th className="table-text-content" onClick={() => handleSort('healthClaimAdvisorId')}>
                                        Health Claim Advisor ID {renderSortIcon('healthClaimAdvisorId')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('lastName')}>
                                        Last Name {renderSortIcon('lastName')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('firstName')}>
                                        First Name {renderSortIcon('firstName')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('name')}>
                                        Firm Account {renderSortIcon('name')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('isActive')}>
                                        Is Active {renderSortIcon('isActive')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('email')}>
                                        Email {renderSortIcon('email')}
                                    </th>
                                    <th className="table-text-content">Enter Portal</th>
                                    <th className="table-text-content" onClick={() => handleSort('relationship')}>
                                        Relationship {renderSortIcon('relationship')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('isNationalDistributionPartner')}>
                                        Nat'l Distro Partner {renderSortIcon('isNationalDistributionPartner')}
                                    </th>
                                    <th className="table-text-content" onClick={() => handleSort('isJg')}>
                                        Is JG {renderSortIcon('isJg')}
                                    </th>
                                    <th className="table-text-content">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentRecords.map((item, index) => (
                                    <tr
                                        key={index}
                                        className={`table-body-item${!item.isActive ? ' advisor-inactive-row' : ''}`}
                                    >
                                        <td className="table-text-content">{item.healthClaimAdvisorId ?? "N/A"}</td>
                                        <td className="table-text-content">{item.lastName ?? "N/A"}</td>
                                        <td className="table-text-content">{item.firstName ?? "N/A"}</td>
                                        <td className="table-text-content">
                                            {item.healthClaimAccountId && item.name
                                                ? `${item.healthClaimAccountId} - ${item.name}`
                                                : "N/A"}
                                        </td>
                                        <td className="table-text-content">{item.isActive ? "Yes" : "No"}</td>
                                        <td className="table-text-content">{item.email ?? "N/A"}</td>
                                        <td className="table-text-content" style={{ whiteSpace: 'nowrap' }}>
                                            {item.userId && item.isActive && (
                                                <AdvisorViewLink target={{ advisorId: item.id }} title="View the advisor portal as this advisor">
                                                    View as User
                                                </AdvisorViewLink>
                                            )}
                                        </td>
                                        <td className="table-text-content">{item.healthClaimRelationship ?? "N/A"}</td>
                                        <td className="table-text-content">
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    title="National Distribution Partner (sales split 20/80)"
                                                    checked={!!item.isNationalDistributionPartner}
                                                    disabled={savingNdpId === item.id}
                                                    onChange={() => requestNdpChange(item)}
                                                />
                                            </div>
                                        </td>
                                        <td className="table-text-content">{item.isJg ? "Yes" : "No"}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <div className="d-flex align-items-center gap-1 flex-nowrap">
                                                <Button variant="outline-primary" size="sm" className="search-action-btn"
                                                    title="Send Email" onClick={() => handleConfirmation(item)}>
                                                    <Mail size={14} /> Email
                                                </Button>
                                                <Button onClick={() => handleShowDetails(item)} variant="primary" size="sm" className="search-action-btn">
                                                    <Eye size={14} /> Details
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                    <div className="search-pagination">
                        <div className="pagination-info">
                            <span>Showing {currentPage * itemsPerPage - itemsPerPage + 1} - {Math.min(currentPage * itemsPerPage, filteredData.length)} entries out of {filteredData.length} entries</span>
                        </div>
                        <CustomPagination
                            nPages={nPages}
                            currentPage={currentPage}
                            setCurrentPage={setCurrentPage}
                        />
                    </div>
                    <Modal show={!!ndpConfirm} onHide={() => setNdpConfirm(null)} className="advisor-confirm-modal">
                        <Modal.Header closeButton>
                            <Modal.Title className="advisor-modal-title">
                                {ndpConfirm?.nextValue ? "Mark as National Distribution Partner" : "Remove National Distribution Partner"}
                            </Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            {ndpConfirm?.nextValue ? (
                                <>
                                    <div>
                                        Mark <b>{ndpConfirm?.item?.firstName} {ndpConfirm?.item?.lastName}</b> as a National Distribution Partner?
                                    </div>
                                    <div className="text-danger mt-2">
                                        Enrollment will be told the sales split is 20/80 for every policy where this advisor is the primary advisor.
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div>
                                        Remove the National Distribution Partner flag from <b>{ndpConfirm?.item?.firstName} {ndpConfirm?.item?.lastName}</b>?
                                    </div>
                                    <div className="text-danger mt-2">
                                        The 20/80 sales split will no longer be shown to enrollment for this advisor.
                                    </div>
                                </>
                            )}
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setNdpConfirm(null)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={confirmNdpChange}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Modal>
                    <Modal show={showToggleConfirmModal} onHide={() => setShowToggleConfirmModal(false)} className="advisor-confirm-modal">
                        <Modal.Header closeButton>
                            <Modal.Title className="advisor-modal-title">Send Email Confirmation</Modal.Title>
                        </Modal.Header>
                        <Modal.Body className="text-danger">
                            This will immediately send the email to the user. Would you like to proceed?
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={() => setShowToggleConfirmModal(false)}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={sendEmail}>
                                Confirm
                            </Button>
                        </Modal.Footer>
                    </Modal>
                </>
            )}
        </>
    );
};

export default AdvisorsReport;
